// Candidate presentations (JT1-1886). These mirror the backend's
// modules/tenant/candidates/presentations/types/presentation.types.ts; the server validates every save.

export enum PresentationKind {
  COVER_PLUS_RESUME = 'cover_plus_resume',
  FORMATTED_RESUME = 'formatted_resume',
  PROFILE = 'profile',
  EXECUTIVE = 'executive',
}

export enum PresentationExportFormat {
  PDF = 'pdf',
  DOCX = 'docx',
  /** Slides (JT1-1887). Not for agency Word layouts. */
  PPTX = 'pptx',
}

export type PresentationFieldKey =
  | 'candidate.full_name'
  | 'candidate.first_name'
  | 'candidate.position_title'
  | 'candidate.email'
  | 'candidate.phone'
  | 'candidate.location'
  | 'candidate.linkedin'
  | 'candidate.availability'
  | 'candidate.notice_period'
  | 'candidate.salary_expectation'
  | 'candidate.current_employer'
  | 'job.title'
  | 'client.name'
  | 'recruiter.name'
  | 'recruiter.email'
  | 'recruiter.phone'
  | 'agency.name'
  | 'agency.phone'
  | 'agency.website'
  | 'document.date';

/** The merge fields offered as chips, most used first. */
export const MERGE_FIELD_KEYS: PresentationFieldKey[] = [
  'candidate.full_name',
  'candidate.position_title',
  'client.name',
  'job.title',
  'recruiter.name',
  'document.date',
  'candidate.location',
  'candidate.availability',
  'candidate.notice_period',
  'candidate.salary_expectation',
  'agency.name',
];

export interface PageSettings {
  size: 'A4' | 'LETTER';
  margins_mm: { top: number; right: number; bottom: number; left: number };
}

export type ImageRef = 'brand' | 'none' | { file_id: string };

export interface ThemeTokens {
  font_family: string;
  base_size_pt: number;
  colors: { primary: string; secondary: string; text: string; muted: string };
  heading: { style: 'plain' | 'underline' | 'bar' | 'filled'; uppercase: boolean };
  header: { image: ImageRef; logo: 'left' | 'right' | 'none'; text?: string | null };
  footer: { image: ImageRef; text?: string | null; page_numbers: 'none' | 'n' | 'n_of_total'; align: 'left' | 'center' | 'right' };
  confidentiality_text?: string | null;
}

export type BlockSource = 'summary' | 'work_experiences' | 'education_history' | 'skills' | 'certifications' | 'industries' | 'languages' | 'memberships' | 'contact' | 'cover';

interface BlockBase {
  id: string;
  hidden?: boolean;
  hide_when_empty?: boolean;
  title?: string | null;
  page_break_before?: boolean;
  keep_together?: boolean;
  source?: BlockSource | null;
  origin?: 'template' | 'profile' | 'manual' | 'ai';
  ai_pending?: boolean;
}

export interface FieldRow { label: string; value: string }
export interface MonthYear { month?: number | null; year?: number | null }

export interface EmploymentItem {
  id: string;
  source_id?: string | null;
  hidden?: boolean;
  employer: string;
  position_title: string;
  location?: string | null;
  start: MonthYear;
  end: MonthYear | null;
  summary_html: string;
}

export interface EducationItem {
  id: string;
  source_id?: string | null;
  hidden?: boolean;
  institution: string;
  qualification: string;
  level?: string | null;
  start_year?: number | null;
  end_year?: number | null;
}

export type PresentationBlock =
  | (BlockBase & { type: 'cover'; heading: string; subheading?: string | null; rows: FieldRow[]; show_logo: boolean; confidentiality_text?: string | null })
  | (BlockBase & { type: 'heading'; text: string; level: 1 | 2 | 3 })
  | (BlockBase & { type: 'rich_text'; role?: 'summary' | 'commentary' | 'free'; html: string })
  | (BlockBase & { type: 'field_list'; layout: 'inline' | 'two_column'; rows: FieldRow[] })
  | (BlockBase & { type: 'employment'; date_format: 'MMM YYYY' | 'YYYY'; items: EmploymentItem[] })
  | (BlockBase & { type: 'education'; items: EducationItem[] })
  | (BlockBase & { type: 'list'; columns: 1 | 2 | 3 | 4; style: 'bullets' | 'plain'; items: string[] })
  | (BlockBase & { type: 'table'; preset?: 'languages' | null; columns: string[]; rows: string[][]; header_row: boolean })
  | (BlockBase & { type: 'image'; file_id: string; width_pct: number; align: 'left' | 'center' | 'right' })
  | (BlockBase & { type: 'divider' })
  | (BlockBase & { type: 'page_break' })
  | (BlockBase & { type: 'original_resume'; candidate_document_id: string | null });

export type PresentationBlockType = PresentationBlock['type'];

export interface PresentationContent {
  schema_version: number;
  page: PageSettings;
  theme_overrides?: Partial<ThemeTokens> | null;
  fields: Partial<Record<PresentationFieldKey, string | null>>;
  snapshot: { source_profile_id: string | null; taken_at: string };
  blocks: PresentationBlock[];
}

export type AnonymisationLevel = 'basic' | 'standard' | 'strict';

/** What an AI draft of a text section does (backend ai/presentation-ai.provider.ts). */
export type SummaryAction = 'generate' | 'tailor_to_job' | 'shorten' | 'expand' | 'improve';
export type ReplacementKind = 'name' | 'email' | 'phone' | 'url' | 'photo' | 'employer' | 'project' | 'location' | 'custom';
export type ReplacementStatus = 'suggested' | 'accepted' | 'edited' | 'rejected';

export interface Replacement {
  id: string;
  kind: ReplacementKind;
  original: string;
  replacement: string;
  status: ReplacementStatus;
  /** 'agency': from the agency's own employer wording (JT1-1887). */
  source: 'rule' | 'ai' | 'manual' | 'agency';
}

export interface ScanFinding {
  block_id: string | null;
  kind: ReplacementKind | 'metadata' | 'flag';
  text: string;
  location: string;
}

export interface AnonymisationState {
  enabled: boolean;
  level: AnonymisationLevel;
  alias: string;
  replacements: Replacement[];
  reviewed_flags?: string[];
  last_scan?: { at: string; findings: ScanFinding[] } | null;
}

export interface PresentationPerson { id: string; first_name: string; last_name: string }

export interface PresentationExport {
  id: string;
  format: PresentationExportFormat;
  presentation_version: number;
  anonymised: boolean;
  file_name: string;
  findings_acknowledged: boolean;
  /** On the application's client portal now: the publication to take down (JT1-1887). */
  publication_id: string | null;
  created_at: string;
  created_by: PresentationPerson | null;
}

/** An export shared with an application's client portal guests, and when it was taken down (JT1-1887). */
export interface PresentationPublication {
  id: string;
  export_id: string;
  application_id: string;
  live: boolean;
  published_at: string;
  published_by: PresentationPerson | null;
  revoked_at: string | null;
  revoked_by: PresentationPerson | null;
}

export interface PresentationSummary {
  id: string;
  title: string;
  kind: PresentationKind;
  template_key: string;
  version: number;
  job: { id: string; title: string } | null;
  application_id: string | null;
  anonymised: boolean;
  created_at: string;
  updated_at: string;
  updated_by: PresentationPerson | null;
  export_count: number;
  latest_export: PresentationExport | null;
  exports: PresentationExport[];
}

export interface Presentation extends PresentationSummary {
  organization_candidate_id: string;
  content: PresentationContent;
  anonymisation: AnonymisationState;
  snapshot_taken_at: string;
  profile_changed: boolean;
  /** The agency Word layout it fills, and that layout's placeholders (JT1-1887). */
  word_template?: { name: string; tags: string[]; revision: number | null; update_available: boolean } | null;
  /** Whether this person may change its design: admins, and staff when the agency allows it (JT1-1887). */
  can_edit_design?: boolean;
}

/** An agency's presentation settings (JT1-1887). */
export interface PresentationSettings {
  /** Staff may change a presentation's design, not only admins. */
  staff_can_edit_design: boolean;
  /** JobTetris template keys and agency template ids that staff can't use. */
  hidden_template_keys: string[];
  /** How shortlist packs and comparisons open. */
  packs: PackSettings;
}

/** How the agency's shortlist packs and comparison reports open (JT1-1887). Admins set it on Settings. */
export interface PackSettings {
  shortlist_heading: string;
  comparison_heading: string;
  /** A note under the cover heading. Empty for none. */
  intro: string;
  show_candidates_table: boolean;
  /** The rows a new comparison starts with, in order. */
  comparison_rows: ComparisonRowKey[];
}

export interface PresentationTemplateSummary {
  key: string;
  name: string;
  description: string | null;
  kind: PresentationKind;
  source: 'system' | 'agency';
  is_default: boolean;
  anonymise_by_default: boolean;
  updated_at: string | null;
  /** 'word' for an agency's uploaded Word layout (JT1-1887). */
  format?: 'blocks' | 'word';
  word_tags?: string[] | null;
  /** The agency template's revision; null for JobTetris's own (JT1-1887). */
  revision?: number | null;
  /** The client company it's for; a presentation for that client's jobs starts on it (JT1-1887). */
  client_company?: { id: string; name: string } | null;
  /** Hidden from staff by an admin (JT1-1887); only admins are sent hidden templates. */
  hidden?: boolean;
}

/** What the server found in an uploaded Word template. */
export interface WordTemplateCheck {
  ok: boolean;
  tags: string[];
  unknown: string[];
  invalid: string[];
  /** Text in braces that isn't a placeholder, such as {provide an example}: it prints as typed (JT1-1887). */
  notes?: string[];
}

/** An uploaded Word file's text, for the placeholder editor (JT1-1887). Paragraphs are numbered per part. */
export interface WordLayoutParagraph { type: 'paragraph'; index: number; text: string }
export interface WordLayoutTable { type: 'table'; index: number; rows: { index: number; cells: { index: number; text: string }[][] }[] }
export interface WordLayoutPart { part: string; kind: 'body' | 'header' | 'footer'; items: (WordLayoutParagraph | WordLayoutTable)[] }

/** Text in the file and the placeholder that replaces it, everywhere it appears. */
export interface WordReplacement { find: string; placeholder: string }

/** A body table row, or body paragraphs from `from` to `to`, repeated for each item of a list. */
export interface WordRepeat {
  list: string;
  table?: number;
  row?: number;
  from?: number;
  to?: number;
  replacements: WordReplacement[];
  /** For a repeated row: the field each cell prints, such as employment.employer; null keeps a cell as it is. */
  columns?: (string | null)[];
  /** For a repeated row: take out the blank rows straight below it. */
  remove_empty_rows_below?: boolean;
}

export interface WordPlaceholderChanges { replacements: WordReplacement[]; repeats: WordRepeat[] }

export interface TemplateDefinition {
  schema_version: number;
  kind: PresentationKind;
  page: PageSettings;
  theme: ThemeTokens;
  blocks: PresentationBlock[];
}

export interface PresentationTemplate extends PresentationTemplateSummary {
  definition: TemplateDefinition;
}

export interface CreatePresentationRequest {
  candidateId: string;
  template_key?: string;
  title?: string;
  application_id?: string;
  anonymise?: boolean;
}

export interface UpdatePresentationRequest {
  id: string;
  version: number;
  title?: string;
  content?: PresentationContent;
  anonymisation?: AnonymisationState;
}

export interface ExportPresentationRequest {
  id: string;
  format: PresentationExportFormat;
  version: number;
  acknowledge_findings?: boolean;
}

export interface ExportPresentationResult {
  id: string;
  format: PresentationExportFormat;
  file_name: string;
  anonymised: boolean;
  presentation_version: number;
  findings: ScanFinding[];
  skipped_attachments: string[];
  download_url: string;
  created_at: string;
}

/** Error codes the builder reacts to (backend presentation.errors.ts). */
export const PresentationErrorCodes = {
  StaleVersion: '29_0101',
  ExportNotAcknowledged: '29_0103',
  RenderBusy: '29_0001',
  AiUnavailable: '29_0106',
  AiFailed: '29_0107',
  AiDraftPending: '29_0108',
  InvalidWordTemplate: '29_0109',
  TemplateHidden: '29_0113',
  DefaultTemplateHidden: '29_0114',
  EmployerWordingAdminOnly: '29_0115',
  ConverterUnavailable: '29_0004',
} as const;

/** The agency's wording for an employer in anonymised presentations (JT1-1887). */
export interface EmployerReplacement {
  id: string;
  employer: string;
  replacement: string;
  updated_at: string;
  updated_by: PresentationPerson | null;
}

/** Several candidates in one document, or one file each, made in the background (JT1-1887). */
export type PresentationPackStatus = 'pending' | 'ready' | 'needs_review' | 'failed';
export type PresentationPackKind = 'shortlist' | 'bulk' | 'comparison';

/** The rows a comparison can show, in their default order (backend COMPARISON_ROWS). */
export const COMPARISON_ROWS = ['title', 'employer', 'location', 'recent_roles', 'education', 'skills', 'languages', 'availability', 'notice_period', 'salary', 'summary'] as const;
export type ComparisonRowKey = typeof COMPARISON_ROWS[number];

export interface PresentationPack {
  id: string;
  kind: PresentationPackKind;
  title: string;
  format: PresentationExportFormat;
  status: PresentationPackStatus;
  options: { rows?: ComparisonRowKey[] } | null;
  candidate_count: number;
  file_name: string | null;
  findings: (ScanFinding & { candidate?: string })[];
  acknowledged: boolean;
  error: string | null;
  created_at: string;
  created_by: PresentationPerson | null;
}

export interface CreatePresentationPackRequest {
  job_id?: string;
  /** In the order they appear in the pack. */
  items: { organization_candidate_id: string; application_id?: string }[];
  /** For candidates who don't have a presentation yet. */
  template_key?: string;
  format: PresentationExportFormat;
  title?: string;
  anonymise?: boolean;
  kind?: PresentationPackKind;
  /** A comparison's rows, in order. */
  comparison_rows?: ComparisonRowKey[];
}

/** The most candidates in one pack (backend MAX_PACK_CANDIDATES). */
export const MAX_PACK_CANDIDATES = 20;
/** The most candidates in one bulk run, one file each (backend MAX_BULK_CANDIDATES). */
export const MAX_BULK_CANDIDATES = 50;
/** A comparison takes 2 to 5 candidates, a column each (backend MAX_COMPARISON_CANDIDATES). */
export const MAX_COMPARISON_CANDIDATES = 5;
